import { logAmplitude } from './analytics';

/**
 * Amplitude events of the challenges (daily activities), so the funnel
 * proposal → acceptance → days → completion can be read per challenge.
 * Every event carries the same activity reference; `from` says which screen
 * fired it: the in-path session step or the activity overview page.
 */
export type ChallengeEventSource = 'session' | 'overview' | 'activity_session';

export interface ChallengeRef {
  _id?: string;
  humanReadableId?: string | null;
  type?: string | null;
  title?: string | null;
  numberOfDays?: number | null;
}

interface ChallengeDay {
  done?: boolean | null;
}

const refParams = (activity?: ChallengeRef | null) => ({
  activityId: activity?._id,
  activity: activity?.humanReadableId || undefined,
  activityType: activity?.type || undefined,
  activityTitle: activity?.title || undefined,
});

const daysDone = (days?: ChallengeDay[] | null): number =>
  (days || []).filter(day => !!day?.done).length;

export const trackChallengeProposed = (activity: ChallengeRef | null | undefined, pathId?: string | null): void => {
  logAmplitude('Challenge Proposed', { ...refParams(activity), pathId: pathId || undefined });
};

export const trackChallengeAccepted = (
  activity: ChallengeRef | null | undefined,
  from: ChallengeEventSource,
  pathId?: string | null,
): void => {
  logAmplitude('Challenge Accepted', { ...refParams(activity), from, pathId: pathId || undefined });
};

export const trackChallengeDeclined = (activity: ChallengeRef | null | undefined, pathId?: string | null): void => {
  logAmplitude('Challenge Declined', { ...refParams(activity), pathId: pathId || undefined });
};

/**
 * A day of the challenge recorded (the first one included). When the day
 * closes the chain, `Challenge Completed` follows so completion can be
 * counted without reading the day property.
 */
export const trackChallengeDayCompleted = (
  activity: ChallengeRef | null | undefined,
  userActivity: { status?: string | null; days?: ChallengeDay[] | null } | null | undefined,
  from: ChallengeEventSource,
): void => {
  const day = daysDone(userActivity?.days);
  const totalDays = userActivity?.days?.length || activity?.numberOfDays || undefined;
  logAmplitude('Challenge Day Completed', { ...refParams(activity), from, day, totalDays });
  if (userActivity?.status === 'completed') {
    logAmplitude('Challenge Completed', { ...refParams(activity), from, totalDays });
  }
};

export const trackChallengeStopped = (
  activity: ChallengeRef | null | undefined,
  from: ChallengeEventSource,
  days?: ChallengeDay[] | null,
): void => {
  logAmplitude('Challenge Stopped', { ...refParams(activity), from, daysDone: daysDone(days) });
};

export const trackChallengeReminderSet = (
  activity: ChallengeRef | null | undefined,
  enabled: boolean,
  time: string | null,
  from: ChallengeEventSource,
): void => {
  logAmplitude('Challenge Reminder Set', { ...refParams(activity), from, enabled, time: enabled ? time : undefined });
};

/** a diary filled outside any challenge (library "free entry") */
export const trackActivityEntryRecorded = (activity: ChallengeRef | null | undefined): void => {
  logAmplitude('Activity Entry Recorded', { ...refParams(activity), mode: 'free' });
};
